import { createContext, useContext, useReducer, ReactNode, useEffect } from 'react'

export type Language = 'ko' | 'en'

interface LanguageState {
  language: Language
}

type LanguageAction = { type: 'SET_LANGUAGE'; payload: Language }

interface LanguageContextType extends LanguageState {
  setLanguage: (language: Language) => void
  toggleLanguage: () => void
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined)

const LANGUAGE_STORAGE_KEY = 'dreamity_language'

const detectSystemLanguage = (): Language => {
  try {
    const nav = window?.navigator
    const primary = (nav?.languages && nav.languages.length > 0 ? nav.languages[0] : nav?.language) || ''
    return String(primary).toLowerCase().startsWith('ko') ? 'ko' : 'en'
  } catch (_) {
    return 'en'
  }
}

const getInitialLanguage = (): Language => {
  const saved = localStorage.getItem(LANGUAGE_STORAGE_KEY) as Language | null
  if (saved === 'ko' || saved === 'en') {
    return saved
  }
  return detectSystemLanguage()
}

const initialState: LanguageState = {
  language: getInitialLanguage(),
}

function languageReducer(
  state: LanguageState,
  action: LanguageAction
): LanguageState {
  switch (action.type) {
    case 'SET_LANGUAGE':
      return { ...state, language: action.payload }
    default:
      return state
  }
}

interface LanguageProviderProps {
  children: ReactNode
}

export function LanguageProvider({ children }: LanguageProviderProps) {
  const [state, dispatch] = useReducer(languageReducer, initialState)

  // localStorage에 언어 저장
  useEffect(() => {
    localStorage.setItem(LANGUAGE_STORAGE_KEY, state.language)
  }, [state.language])

  const setLanguage = (language: Language) => {
    dispatch({ type: 'SET_LANGUAGE', payload: language })
  }

  const toggleLanguage = () => {
    const newLanguage = state.language === 'ko' ? 'en' : 'ko'
    dispatch({ type: 'SET_LANGUAGE', payload: newLanguage })
  }

  return (
    <LanguageContext.Provider
      value={{
        ...state,
        setLanguage,
        toggleLanguage,
      }}
    >
      {children}
    </LanguageContext.Provider>
  )
}

export function useLanguageContext() {
  const context = useContext(LanguageContext)
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider')
  }
  return context
}

